import { memo, useEffect, useState } from "react";

import dayjs from "dayjs";

import Clock from "@zenith/assets/icons/Clock";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import GreenTick from "@zenith/assets/icons/components/GreenTick";
import CircularProgress from "@mui/material/CircularProgress";
import {
  DatepickerComponent,
  DropZoneComponent,
  ModalComponent,
  SelectComponent,
  SpinnerComponent,
  TextFieldComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import { useNotificationContext, useUpgradeContext } from "@zenith/context";
import {
  CONNECTION_ERROR_MESSAGE,
  DOCUMENTS_JOB_ID,
  MIN_SIZE_BACK_OFFICE,
} from "@zenith/data/constants";
import StepStatusComponent from "@zenith/features/Tiers/components/StepStatusComponent/step-status-component/step-status-component";
import { AppNavigation, AppStepper, useDocumentVerification, TierStepper } from "@zenith/hooks";
import { UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import {
  ApiResponseDTODiscardUpgradeResponseDTO,
  ApiResponseDTODocumentStatusResponseDTO,
  ApiResponseDTONINVerificationResponseDTO,
  DocumentApiService,
  DocumentDTO,
  DocumentStatusResponseDTO,
  ImageRequestDTO,
  RequiredFieldDTO,
  SubmitDocumentRequestDTO,
  TierApiService,
  TierStepsResponseDTO,
  UsersApiService,
} from "@zenith/services/user";
import {
  getFileExtension,
  getMinExpiryDate,
  removeBase64Prefix,
  updateTierStepStatus,
} from "@zenith/utils";
import { getErrorTitle } from "@zenith/utils/Helpers/getAPIErrorTitle";
import CheckCircle from "@zenith/assets/icons/CheckCircle";

export interface DocumentVerificationPageProps {
  step: TierStepsResponseDTO;
}

const DocumentVerificationPage = memo(function DocumentVerificationPage(
  props: DocumentVerificationPageProps
) {
  const {
    step: { stepStatus, rejectionReason },
  } = props;

  const { navigateTo } = AppNavigation();
  const { setActiveStep } = AppStepper();
  const { goToNextOpenStep } = TierStepper();
  const { upgradeContext, setUpgradeContext, clearUpgradeContext } =
    useUpgradeContext();
  const { setOpen } = useNotificationContext();
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({ title: "", message: "", status: "success" });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({ title, message, status });
    setOpen(true);
  }

  const documentsJobId = sessionStorage.getItem(DOCUMENTS_JOB_ID);
  const [initialCalendarOpen, setInitialCalendarOpen] =
    useState<boolean>(false);
  const [documents, setDocuments] = useState<DocumentDTO[] | undefined>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentDTO>();
  const [selectedDocumentError, setSelectedDocumentError] = useState<{
    hasError: boolean;
    message: string;
  }>({ hasError: false, message: "" });
  const [selectItems, setSelectItems] = useState<SelectItemsType[]>([]);
  const [documentsArray, setDocumentsArray] = useState<ImageRequestDTO[]>([]);
  const [formData, setFormData] = useState<RequiredFieldDTO | undefined>();
  const [formDataErrors, setFormDataErrors] =
    useState<Record<string, { hasError: boolean; message: string }>>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [secondErrorMessage, setSecondErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [backModal, setBackModal] = useState<boolean>(false);
  const [jobId, setJobId] = useState<string | undefined>(
    documentsJobId ? JSON.parse(documentsJobId) : ""
  );
  const [documentStatus, setDocumentStatus] = useState<string | undefined>(
    stepStatus
  );
  const [documentRejectionReason, setDocumentRejectionReason] = useState<
    string | undefined
  >(rejectionReason);
  const [isDocumentSubmitted, setIsDocumentSubmitted] = useState<boolean>(
    stepStatus !== TierStepsResponseDTO.stepStatus.NOT_STARTED ||
      !!documentsJobId
  );
  const [verifyingDocument, setVerifyingDocument] = useState<boolean>(
    !!documentsJobId
  );
  const [pollingActive, setPollingActive] = useState<boolean>(!!documentsJobId);
  const [isContinueDisabled, setIsContinueDisabled] = useState<boolean>(false);
  const [documentNumber, setDocumentNumber] = useState<string>("");

  useEffect(() => {
    if (
      !selectItems.length &&
      documentStatus !==
        (TierStepsResponseDTO.stepStatus.APPROVED ||
          TierStepsResponseDTO.stepStatus.PENDING)
    ) {
      DocumentApiService.getDocumentSelect(
        "NIGERIA_DOCUMENT_VERIFICATION"
      ).then((res) => {
        if (
          res.data &&
          res.data.documents &&
          selectItems &&
          selectItems.length === 0
        ) {
          const newSelectItemsArray: SelectItemsType[] = [];
          res.data.documents.forEach((e) => {
            newSelectItemsArray.push({
              label: e.text || "",
              value: e.code || "",
            });
          });
          setSelectItems(newSelectItemsArray);
          setDocuments(res.data.documents);
        }
      });
    }
  }, [selectItems]);

  const onSelectChange = (
    event: React.SyntheticEvent<Element, Event> | null,
    newValue: string | null
  ) => {
    const newSelectedDocument = documents?.find(
      (item) => item.code === newValue
    );

    setDocumentsArray([]);

    setFormData(() => undefined);
    setFormDataErrors(() => undefined);

    setSelectedDocument(newSelectedDocument);

    if (newSelectedDocument) {
      const initialFormDataErrors: Record<
        string,
        { hasError: boolean; message: string }
      > = {};

      const initialFormData: RequiredFieldDTO | undefined =
        newSelectedDocument.requiredFields?.reduce(
          (acc: RequiredFieldDTO | undefined, curr) => {
            let initialValue: string | undefined;

            switch (curr.type) {
              case "string":
                initialValue = "";
                break;
              case "date":
              case "file":
                initialValue = undefined;
                break;
              default:
                initialValue = "";
            }

            if (acc && curr.fieldCode) {
              const fieldText = curr.fieldText as keyof RequiredFieldDTO;
              (acc[fieldText] as string | undefined) = initialValue;

              initialFormDataErrors[fieldText] = {
                hasError: false,
                message: "",
              };
            }

            return acc;
          },
          {}
        );

      if (initialFormData) {
        setFormData(() => initialFormData);
        setFormDataErrors(() => initialFormDataErrors);
      }
    }
  };

  useEffect(() => {
    if (selectedDocument && selectedDocument.code === "NATIONAL_ID_CARD") {
      UsersApiService.getNinVerificationStatus()
        .then(({ data }: ApiResponseDTONINVerificationResponseDTO) => {
          const documentNumberField = selectedDocument.requiredFields?.find(
            (field) => field.fieldCode === "DOCUMENT_NUMBER_FIELD"
          );

          if (data?.nationalIdentificationNumber && documentNumberField) {
            onFieldChange(
              data.nationalIdentificationNumber,
              documentNumberField
            );
          }
        })
        .catch((error) => {
          return error;
        });
    }
  }, [selectedDocument]);

  const onSelectBlur = (newValue: string) => {
    if (!newValue) {
      setSelectedDocumentError({
        hasError: true,
        message: "Identification Document is required",
      });
      return;
    }
    setSelectedDocumentError({
      hasError: false,
      message: "",
    });
  };

  const onFieldChange = (value: string, field: RequiredFieldDTO) => {
    let fieldValue = value;

    // Check with the BE to change the field type to the correct type
    // and instead of checking field.fieldText we will check field.type
    if (field.fieldText === "National Identification Number (NIN/NIMC)") {
      fieldValue = fieldValue.replace(/\D/g, "");
    }

    setFormDataErrors({
      ...formDataErrors,
      [field.fieldText as keyof RequiredFieldDTO]: {
        hasError: false,
        message: "",
      },
    });

    setFormData({
      ...formData,
      [field.fieldText as keyof RequiredFieldDTO]: fieldValue,
    });

    setDocumentNumber(fieldValue);
  };

  const onFieldBlur = (
    event: React.FocusEvent<HTMLInputElement, Element>,
    field: RequiredFieldDTO
  ) => {
    const regex = new RegExp(field.validationRule || "");
    if (event.currentTarget.value === "") {
      setFormDataErrors({
        ...formDataErrors,
        [field.fieldText as keyof RequiredFieldDTO]: {
          hasError: true,
          message: field.missingErrorText as string,
        },
      });
      return;
    }
    if (!regex.test(event.currentTarget.value)) {
      setFormDataErrors({
        ...formDataErrors,
        [field.fieldText as keyof RequiredFieldDTO]: {
          hasError: true,
          message: field.invalidErrorText as string,
        },
      });
    }
  };

  const onDateFieldChange = (event: any, field: RequiredFieldDTO): void => {
    if (!event || !event.$d) return;
    const expirationDate = event.$d;

    setFormDataErrors({
      ...formDataErrors,
      [field.fieldText as keyof RequiredFieldDTO]: {
        hasError: false,
        message: "",
      },
    });
    setFormData({
      ...formData,
      [field.fieldText as keyof RequiredFieldDTO]: expirationDate,
    });
  };

  const onDateFieldBlur = (
    event: { $d?: string | number | undefined },
    field: RequiredFieldDTO
  ) => {
    if (
      !event ||
      event.$d === undefined ||
      String(event.$d) === "Invalid Date"
    ) {
      setFormDataErrors({
        ...formDataErrors,
        [field.fieldText as keyof RequiredFieldDTO]: {
          hasError: true,
          message: field.missingErrorText as string,
        },
      });
      return;
    }

    const expirationDate = new Date(event.$d);
    const day = String(expirationDate.getDate()).padStart(2, "0");
    const month = String(expirationDate.getMonth() + 1).padStart(2, "0");
    const year = expirationDate.getFullYear();
    const regex = new RegExp(field.validationRule?.replace(/`/g, "") || "");

    setFormDataErrors({
      ...formDataErrors,
      [field.fieldText as keyof RequiredFieldDTO]: {
        hasError: !regex.test(`${day}/${month}/${year}`),
        message: field.invalidErrorText as string,
      },
    });

    setFormData({
      ...formData,
      [field.fieldText as keyof RequiredFieldDTO]: expirationDate,
    });
  };

  const getImage = (image: File | null, field: RequiredFieldDTO) => {
    const reader = new FileReader();
    if (!image) return;
    reader.readAsDataURL(image);
    reader.onload = function () {
      setFormData({
        ...formData,
        [field.fieldText as keyof RequiredFieldDTO]: reader.result,
      });

      // Add the image to the images array
      const newImagesArray = documentsArray;
      const mimeExt = getFileExtension(image);
      newImagesArray.push({
        imageType: field.fieldCode as ImageRequestDTO.imageType,
        imageContent: removeBase64Prefix(reader.result as string, mimeExt),
        mimeExt: getFileExtension(image),
      });
      setDocumentsArray(newImagesArray);
    };
  };

  const deleteImage = (field: RequiredFieldDTO) => {
    setFormData({
      ...formData,
      [field.fieldText as keyof RequiredFieldDTO]: undefined,
    });

    // Delete the image from the array
    const filteredImages = documentsArray.filter(
      (document) => document.imageType !== field.fieldCode
    );
    setDocumentsArray(filteredImages);
  };

  const formDataIsUndefinedOrEmpty: boolean = formData
    ? Object.values(formData).some(
        (value) => value === undefined || value === ""
      )
    : false;

  const formHasErrors: boolean = formDataErrors
    ? Object.values(formDataErrors).some((error) => error.hasError)
    : false;

  const handleTryAgain = () => {
    setJobId("");
    setPollingActive(false);
    setIsDocumentSubmitted(false);
  };

  const handleModalState = () => {
    setBackModal(!backModal);
  };

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const handleContinueButton = () => {
    goToNextOpenStep();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const request: SubmitDocumentRequestDTO = {
      documentNumber: documentNumber,
      documentType:
        selectedDocument?.code as SubmitDocumentRequestDTO.documentType,
      images: documentsArray as ImageRequestDTO[],
      ...((selectedDocument?.code === "DRIVERS_LICENSE" ||
        selectedDocument?.code === "INTERNATIONAL_PASSPORT") && {
        expiryDate: dayjs(
          formData?.["Expiry Date" as keyof RequiredFieldDTO]
        ).format("YYYY-MM-DD"),
      }),
    };

    setIsContinueDisabled(true);

    if (
      upgradeContext.isRecoveryFlow &&
      documentStatus !== TierStepsResponseDTO.stepStatus.REJECTED
    ) {
      return;
    }

    UsersApiService.postDocumentVerificationMultipart1(request)
      .then((response) => {
        setIsContinueDisabled(false);
        if (response.data?.jobId) {
          setJobId(response.data.jobId);
          sessionStorage.setItem(
            DOCUMENTS_JOB_ID,
            JSON.stringify(response.data.jobId)
          );
          setIsDocumentSubmitted(true);
          setVerifyingDocument(true);
          setPollingActive(true);
        }
      })
      .catch((error) => {
        const message = error?.body?.message || CONNECTION_ERROR_MESSAGE;
        const title = getErrorTitle(error?.status);
        notify(title, message, "error");
        setIsContinueDisabled(false);
      });
  };

  // Helper function to map document status to step status
  const mapDocumentStatusToStepStatus = (
    status: string | undefined
  ): TierStepsResponseDTO.stepStatus => {
    if (!status) return TierStepsResponseDTO.stepStatus.NOT_STARTED;

    switch (status) {
      case DocumentStatusResponseDTO.status.VERIFIED_SUCCESSFULLY:
        return TierStepsResponseDTO.stepStatus.APPROVED;
      case DocumentStatusResponseDTO.status.ERROR_IN_VERIFICATION:
        return TierStepsResponseDTO.stepStatus.REJECTED;
      case DocumentStatusResponseDTO.status.PENDING_VERIFICATION:
        return TierStepsResponseDTO.stepStatus.PENDING;
      case DocumentStatusResponseDTO.status.NEEDS_MANUAL_VERIFICATION:
        return TierStepsResponseDTO.stepStatus.PENDING;
      default:
        return TierStepsResponseDTO.stepStatus.NOT_STARTED;
    }
  };

  // Use our new specialized hook for document verification
  const {
    status: documentVerificationStatus,
    error,
    reset,
  } = useDocumentVerification(pollingActive, true, jobId as string, true);

  // Process document verification response
  useEffect(() => {
    if (!documentVerificationStatus) return;

    setPollingActive(false);
    setVerifyingDocument(false);

    setDocumentStatus(documentVerificationStatus);

    const tierStepsUpdated = updateTierStepStatus(
      upgradeContext,
      TierStepsResponseDTO.stepName.DOCUMENT_VERIFICATION_STEP,
      mapDocumentStatusToStepStatus(documentVerificationStatus)
    );

    const newContext = { ...upgradeContext, tierSteps: tierStepsUpdated };
    if (
      JSON.stringify(newContext.tierSteps) !==
      JSON.stringify(upgradeContext.tierSteps)
    ) {
      setUpgradeContext(newContext);
    }

    const messageFromStatus = getErrorMessageForDocumentStatus(
      documentVerificationStatus
    );
    if (messageFromStatus) {
      setErrorMessage(messageFromStatus.primary);
      setSecondErrorMessage(messageFromStatus.secondary || "");
    }
  }, [documentVerificationStatus, upgradeContext, setUpgradeContext]);
  // Stop polling on timeout or network error
  useEffect(() => {
    if (error) {
      setPollingActive(false);
      setVerifyingDocument(false);
    }
  }, [error]);

  // Helper function to get error messages from status
  const getErrorMessageForDocumentStatus = (status: string | undefined) => {
    if (!status) return null;

    // Tier step statuses are not document-specific errors — skip them
    const tierStepStatuses: string[] = Object.values(
      TierStepsResponseDTO.stepStatus
    );
    if (tierStepStatuses.includes(status)) return null;

    // Map from status to user-friendly message
    const statusToMessage: Record<
      string,
      { primary: string; secondary?: string }
    > = {
      WRONG_DOCUMENT_TYPE: {
        primary: "The uploaded document is of an incorrect type.",
      },
      EXPIRED_DOCUMENT: {
        primary: "The document has expired.",
      },
      SELFIE_DOCUMENT_PHOTO_MISMATCH: {
        primary: "The selfie did not correspond to the photo on the document.",
      },
      GENERAL_ERROR_IN_VERIFICATION: {
        primary:
          "Document verification failed due to an unusable or invalid image (e.g., work ID card).",
      },
    };

    return (
      statusToMessage[status] || {
        primary: "It was not possible to verify your document",
        secondary: "Please try again.",
      }
    );
  };

  // Use DEBUG flag for consistent logging across the app
  const DEBUG = process.env.NODE_ENV === "development";
  const log = (...args: any[]) =>
    DEBUG && console.log("[DocumentVerificationPage]", ...args);

  // Reset polling when component unmounts
  useEffect(() => {
    log("Setting up document verification polling with jobId:", jobId);
    return () => {
      log("Cleaning up document verification polling");
      reset();
    };
  }, [reset, jobId]);

  return (
    <form onSubmit={handleSubmit}>
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Discard Upgrade"
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={handleModalState}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />
      {isDocumentSubmitted ? (
        <div className="mt-6 flex flex-col">
          <h1 className="mb-6 text-4.5xl font-medium text-neutral-900">
            Document Verification
          </h1>

          {!verifyingDocument &&
            (documentStatus ===
              DocumentStatusResponseDTO.status.PENDING_VERIFICATION ||
              documentStatus === TierStepsResponseDTO.stepStatus.PENDING) && (
              <StepStatusComponent
                status={DocumentStatusResponseDTO.status.PENDING_VERIFICATION}
                title={"Waiting For Approval"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<Clock transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}

          {!verifyingDocument &&
            (documentStatus ===
              DocumentStatusResponseDTO.status.VERIFIED_SUCCESSFULLY ||
              documentStatus === TierStepsResponseDTO.stepStatus.APPROVED) && (
              <StepStatusComponent
                status={DocumentStatusResponseDTO.status.VERIFIED_SUCCESSFULLY}
                title={"Document Successfully Verified"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<CheckCircle transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}

          {!verifyingDocument &&
            (documentStatus ===
              DocumentStatusResponseDTO.status.ERROR_IN_VERIFICATION ||
              documentStatus === TierStepsResponseDTO.stepStatus.REJECTED) && (
              <StepStatusComponent
                status={DocumentStatusResponseDTO.status.ERROR_IN_VERIFICATION}
                title={"Document Verification Failed"}
                subTitle={"Please try again."}
                icon={<CloseCircle transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleTryAgainButton={handleTryAgain}
                errorTitle="Document Verification"
                errorMessage={errorMessage}
                secondErrorMessage={
                  documentRejectionReason || secondErrorMessage
                }
              />
            )}

          {verifyingDocument && (
            <div className="mt-10 lg:ml-10 lg:flex">
              <div className="flex flex-col items-center">
                <SpinnerComponent size={64} type={"primary"} showIcon={false} />
                <p className="mt-6 text-base font-normal text-neutral-600">
                  Your document is being verified.
                </p>
                <p className="text-sm font-normal text-neutral-400">
                  This should not take too long.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="mb-12 overflow-hidden">
            <h5 className="mb-4 text-4xl font-medium">Document Verification</h5>
            {selectedDocument ? (
              <div className="text-base">
                Select the type of identification and ensure you have the
                necessary documents for verification
              </div>
            ) : (
              <div className="text-base">
                Choose the document type and make sure you have the necessary
                documents for verification
              </div>
            )}
          </div>
          <div className="mb-4">
            <SelectComponent
              items={selectItems}
              className="w-full rounded-xl"
              listboxZIndex={1300}
              label="Identification Document"
              id="identificationDocument"
              defaultValue=""
              labelId="identificationDocumentLabel"
              placeholder="Select identification document"
              hasError={selectedDocumentError.hasError}
              errorMessage={selectedDocumentError.message}
              onChange={onSelectChange}
              onClose={onSelectBlur}
            />
          </div>
          <div>
            {selectedDocument?.requiredFields?.map((field) => {
              if (field.type === "string") {
                return (
                  <div className="mb-6" key={field.fieldText}>
                    <TextFieldComponent
                      id={
                        field.fieldText
                          ?.replace(/ /g, "-")
                          .toLocaleLowerCase() || ""
                      }
                      labelId={
                        field.fieldText?.replace(/ /g, "-").toLowerCase() +
                          "-label" || ""
                      }
                      className="grow"
                      containerClassName="flex grow"
                      maxLength={field.maxTextLength}
                      hasError={
                        formDataErrors?.[
                          field.fieldText as keyof RequiredFieldDTO
                        ].hasError
                      }
                      label={field.fieldText || ""}
                      placeholder={field.hintText || ""}
                      type={
                        field.fieldText ===
                        "National Identification Number (NIN/NIMC)"
                          ? "tel"
                          : "text"
                      }
                      value={
                        formData?.[field.fieldText as keyof RequiredFieldDTO] ||
                        ""
                      }
                      onChange={(event) =>
                        onFieldChange(event.target.value, field)
                      }
                      onBlur={(event) => onFieldBlur(event, field)}
                    />
                    {formDataErrors?.[field.fieldText as keyof RequiredFieldDTO]
                      .hasError && (
                      <div className="mt-1 ml-2 text-primary-250">
                        <span className="break-words text-xs">
                          {
                            formDataErrors?.[
                              field.fieldText as keyof RequiredFieldDTO
                            ].message
                          }
                        </span>
                      </div>
                    )}
                  </div>
                );
              }

              if (field.type === "date") {
                return (
                  <div className="mb-7" key={field.fieldText}>
                    <DatepickerComponent
                      id={
                        field.fieldText
                          ?.replace(/ /g, "-")
                          .toLocaleLowerCase() || ""
                      }
                      labelId={
                        field.fieldText?.replace(/ /g, "-").toLowerCase() +
                          "-label" || ""
                      }
                      className="[&>label]:text-main-200-50 w-full"
                      format="DD/MM/YYYY"
                      onOpen={() => setInitialCalendarOpen(true)}
                      onClose={() => setInitialCalendarOpen(false)}
                      isOpen={initialCalendarOpen}
                      label={field.fieldText || ""}
                      formatDensity="spacious"
                      showCalendarIcon={false}
                      minDate={getMinExpiryDate()}
                      disableOpenPicker={false}
                      hasError={
                        formDataErrors?.[
                          field.fieldText as keyof RequiredFieldDTO
                        ].hasError || false
                      }
                      value={
                        formData?.[field.fieldText as keyof RequiredFieldDTO] ||
                        ""
                      }
                      onChange={(event) => onDateFieldChange(event, field)}
                      validationMessage={""}
                      slotProps={{
                        field: {
                          onKeyDown: (event) => event.stopPropagation(),
                          onBlur: () =>
                            onDateFieldBlur(
                              {
                                $d: formData?.[
                                  field.fieldText as keyof RequiredFieldDTO
                                ],
                              },
                              field
                            ),
                        },
                        textField: {
                          onKeyDown: (event) => event.preventDefault(),
                          onClick: () => {
                            setInitialCalendarOpen(!initialCalendarOpen);
                          },
                          error: false,
                          placeholder: "Select a date",
                          focused: false,
                        },
                      }}
                    />
                    {formDataErrors?.[field.fieldText as keyof RequiredFieldDTO]
                      .hasError && (
                      <div className="mt-1 ml-2 text-primary-250">
                        <span className="break-words text-xs">
                          {
                            formDataErrors?.[
                              field.fieldText as keyof RequiredFieldDTO
                            ].message
                          }
                        </span>
                      </div>
                    )}
                  </div>
                );
              }
              return "";
            })}
            {selectedDocument && (
              <div className="mb-8">
                <h4 className="mb-1 text-lg font-bold">
                  Upload Identification Document
                </h4>
                <p className="text-sm font-extralight text-neutral-400">
                  {selectedDocument.code === "NATIONAL_ID_CARD"
                    ? "Minimum file size is 50KB, Maximum file size is 10MB, and the file type must be JPEG."
                    : "Maximum file size is 10MB, and the file type must be JPEG."}
                  Ensure the document is still valid.
                </p>
              </div>
            )}
            {selectedDocument?.requiredFields?.map((field) => {
              if (field.type === "file") {
                return (
                  <div className="mb-6 cursor-pointer" key={field.fieldText}>
                    <DropZoneComponent
                      fieldName={field.fieldText || ""}
                      getImage={(image: File | null) => getImage(image, field)}
                      deleteImage={() => deleteImage(field)}
                      parentFileChangeListener={selectedDocument}
                      documentType={selectedDocument.code}
                      acceptedMinSize={MIN_SIZE_BACK_OFFICE}
                      fileInvalidTypeErrorMessage={
                        selectedDocument.code === "NATIONAL_ID_CARD"
                          ? "File type must be JPEG (min 50KB)"
                          : "File type must be JPEG"
                      }
                    />
                  </div>
                );
              }
              return "";
            })}
          </div>
          <div className="stepper-buttons-container">
            <button
              id="back-button"
              className="secondary-button"
              onClick={handleModalState}
              type="button"
            >
              Discard
            </button>
            <button
              id="continue-button"
              className="primary-button"
              type="submit"
              disabled={
                !selectedDocument ||
                formDataIsUndefinedOrEmpty ||
                formHasErrors ||
                isContinueDisabled ||
                (selectedDocument.code === "NATIONAL_ID_CARD" &&
                  !documentsArray.some(
                    (doc) =>
                      doc.imageType ===
                      ImageRequestDTO.imageType.DOCUMENT_FRONT_BASE64
                  ))
              }
            >
              {isContinueDisabled ? (
                <CircularProgress className="text-zinc-600" size={"16px"} />
              ) : (
                "Continue"
              )}
            </button>
          </div>
        </div>
      )}
    </form>
  );
});

export default DocumentVerificationPage;
